// Central builders for the SPA's in-app navigation paths. Every screen and test
// constructs zone/home URLs through here, so the route shape lives in exactly one
// place and each dynamic segment is consistently URL-encoded: prtId is a device
// (zigbee) address and homeId an account id, neither guaranteed URL-safe.
//
// The router's useParams() returns these segments already decoded, so the
// validators compare on the decoded strings — see zoneValidation.js.

const enc = encodeURIComponent;

// The signed-in landing. It names no home: it resolves the remembered / default
// home and redirects to that home's Property page (homePath), so a bookmark of the
// root always lands somewhere. The route guards also bounce here when a URL names
// a home or zone the account does not own.
export const PROPERTY_PATH = '/';

// A home's Property page. The home is IN the URL, so each tab, bookmark and history
// entry owns its own context — nothing about which home is shown is shared state.
// The segment says "property", the app's word for what the API calls a home (the
// identifiers here keep the API's name; only user-visible text uses the app's).
export function homePath(homeId) {
  return `/property/${enc(homeId)}`;
}

// The route PATTERN the home layout is registered under (App.jsx). A guard in that
// layout uses it with useMatch to tell the Property index from the action screens
// nested below it, which is encoding-proof where comparing pathname strings is not.
export const HOME_ROUTE_PATTERN = '/property/:homeId';

// A zone-detail screen. zoneId is the darwin_core tuple [prtId, endpointId]
// (endpoint 5 = central heating, 6 = hot water).
export function zonePath(homeId, zoneId) {
  const [prtId, endpointId] = zoneId;
  return `/zone/${enc(homeId)}/${enc(prtId)}/${enc(endpointId)}`;
}

// A dedicated action screen nested under a zone: 'status' (heating status /
// mode-select), 'schedule' (view-only), or 'boost' (plus hours).
export function zoneActionPath(homeId, zoneId, action) {
  return `${zonePath(homeId, zoneId)}/${action}`;
}

// A property-level action screen nested under a home: 'holiday' or 'data'.
export function homeActionPath(homeId, action) {
  return `${homePath(homeId)}/${action}`;
}

// A single alarm's detail screen, deep-linkable under the Data screen. `alarmId`
// is the fault's raisedAt (unique per event); the detail screen re-finds the live
// fault by it so a direct link resolves the same alarm.
export function alarmDetailPath(homeId, alarmId) {
  return `${homePath(homeId)}/data/alarm/${enc(alarmId)}`;
}

// The Help section (the app's HelpMenu), opened from the Property screen's menu.
// Account-level rather than per home, so it names no home in its address.
export const HELP_PATH = '/help';

// The Help section's pages, mirroring the app's HelpMenu: the two link lists, and
// the two pilot-only sections with a page per topic / question.
export const HELP_VIDEOS_PATH = '/help/videos';
export const HELP_FAQS_PATH = '/help/faqs';

// Watch App help: the topic list, and one topic (by the slug in help/watchTopics).
export const WATCH_HELP_PATH = '/help/watch';
export function watchHelpTopicPath(slug) {
  return `${WATCH_HELP_PATH}/${enc(slug)}`;
}

// Demand Side Response FAQs: the question list, and one question. The URL carries
// the app's own 1-based numbering (its screens are DsrFaq1…), so `index` here is
// the 0-based position in the shared question list.
export const DSR_FAQ_PATH = '/help/dsr';
export function dsrFaqPath(index) {
  return `${DSR_FAQ_PATH}/${index + 1}`;
}

// The Legal section — the Terms & Conditions and the Privacy Notice, opened from
// the Property screen's menu. Public rather than gated (see legal/LegalScreen.jsx
// and App.jsx): a legal page should be linkable without an account, and the
// stale-terms notice links here from a state where the gate is showing that
// notice instead of any screen.
export const LEGAL_PATH = '/legal';
